import React, { useMemo } from "react";
import { gql, useQuery } from "urql";
import { Link } from "wouter";
import { sortBy } from "lodash";

import { Page } from "../../../components/src/Page";
import { ReactTable2 } from "../../../components/src/react-table2/ReactTable";
import { numberFormatFun, twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateForm } from "../../../components/src/dateForm";
import { useQueryState } from "../../../components/src/use-query-state";
import { DateHelper } from "../../../common/src/calendar/DateHelper";
import { InstrumentModelTypeEnum, InstrumentPosition, IssuerProgramCategory, Party } from "../types.generated";
import { toSpreadBp, toYieldPct } from "./bondsDisplayUnits";

const spreadBpFormat = numberFormatFun("# ##0");
const yieldPctFormat = numberFormatFun("# ##0.00");

const GET_BONDS_IN_POSITION = gql`
    query bondsInPosition($endDate: GraphQLDateString, $filter: PositionFilter) {
        allInstrumentsInPosition(endDate: $endDate, modelTypes: Bond) {
            instrumentId
            clients {
                _id
                name
            }
            instrument {
                _id
                name
                isin
                modelSubType
                currency
                maturityDate
                creditRating
                issuerId
                issuer {
                    _id
                    name
                }
                issuerProgramId
                issuerProgram {
                    _id
                    category
                }
                zeroSpread(date: $endDate)
                yield(date: $endDate)
            }
        }
        tradingmanager(filterZeroPositions: true, filter: $filter, lookThrough: false) {
            instrumentId
            quantity
            modelType
        }
    }
`;

interface BondRow {
    instrumentId: string;
    name: string;
    isin: string | null;
    modelSubType: string | null;
    currency: string | null;
    issuerId: string | null;
    issuerName: string | null;
    creditRating: string | null;
    issuerProgramId: string | null;
    issuerProgramCategory: string | null;
    maturityDate: string | null;
    spread: number | null; // basis points
    yield: number | null; // percent
    totalQuantity: number;
    clients: { _id: string; name: string }[];
    clientNames: string[];
}

const getPreviousBusinessDay = (date: Date): Date => {
    const day = date.getDay();
    if (day === 0) return DateHelper.addDays(date, -2);
    if (day === 1) return DateHelper.addDays(date, -3);
    if (day === 6) return DateHelper.addDays(date, -1);
    return DateHelper.addDays(date, -1);
};

export const BondsPage = (): React.ReactElement => {
    const [date] = useQueryState("date", getPreviousBusinessDay(new Date()).toISOString().slice(0, 10));

    const [{ fetching, error, data }] = useQuery({
        query: GET_BONDS_IN_POSITION,
        variables: { endDate: date, filter: { endDate: date } },
        pause: !date || !/^\d{4}-\d{2}-\d{2}$/.test(date),
        requestPolicy: "cache-and-network"
    });

    const totalQuantityByInstrument = useMemo(() => {
        const totals: Record<string, number> = {};
        for (const position of data?.tradingmanager ?? []) {
            if (position.modelType !== InstrumentModelTypeEnum.Bond || !position.instrumentId) {
                continue;
            }
            const instrumentId = String(position.instrumentId);
            totals[instrumentId] = (totals[instrumentId] ?? 0) + (typeof position.quantity === "number" ? position.quantity : 0);
        }
        return totals;
    }, [data?.tradingmanager]);

    const bondRows: BondRow[] = useMemo(() => {
        if (!data?.allInstrumentsInPosition) {
            return [];
        }

        const positions = data.allInstrumentsInPosition as InstrumentPosition[];
        const rows: BondRow[] = [];

        for (const position of positions) {
            const instrument = position.instrument;
            if (!instrument) {
                continue;
            }

            const clients = sortBy(
                (position.clients ?? [])
                    .filter((client): client is Party => Boolean(client?._id && client?.name))
                    .map((client) => ({ _id: String(client._id), name: client.name })),
                "name"
            );

            rows.push({
                instrumentId: String(instrument._id ?? position.instrumentId),
                name: instrument.name ?? "",
                isin: instrument.isin ?? null,
                modelSubType: instrument.modelSubType ?? null,
                currency: instrument.currency ?? null,
                issuerId: instrument.issuerId ? String(instrument.issuerId) : null,
                issuerName: instrument.issuer?.name ?? null,
                creditRating: instrument.creditRating ?? null,
                issuerProgramId: instrument.issuerProgramId ? String(instrument.issuerProgramId) : null,
                issuerProgramCategory: instrument.issuerProgram?.category ?? null,
                maturityDate: instrument.maturityDate ?? null,
                spread: typeof instrument.zeroSpread === "number" ? toSpreadBp(instrument.zeroSpread) : null,
                yield: typeof instrument.yield === "number" ? toYieldPct(instrument.yield) : null,
                totalQuantity: totalQuantityByInstrument[String(instrument._id ?? position.instrumentId)] ?? 0,
                clients,
                clientNames: clients.map((client) => client.name)
            });
        }

        return sortBy(rows, "name");
    }, [data, totalQuantityByInstrument]);

    const columns = useMemo(
        () => [
            {
                header: "Name",
                accessorKey: "name",
                cell: (cellProps: { row: { original: BondRow }; getValue: () => string }) => (
                    <Link to={`/instruments/${cellProps.row.original.instrumentId}`} target="_blank">
                        {cellProps.getValue()}
                    </Link>
                )
            },
            {
                header: "ISIN",
                accessorKey: "isin",
                cell: (cellProps: { getValue: () => string | null }) => cellProps.getValue() || "N/A"
            },
            {
                header: "Sub type",
                accessorKey: "modelSubType",
                cell: (cellProps: { getValue: () => string | null }) => cellProps.getValue() || "N/A"
            },
            {
                header: "CCY",
                accessorKey: "currency",
                cell: (cellProps: { getValue: () => string | null }) => cellProps.getValue() || "N/A"
            },
            {
                header: "Issuer",
                accessorKey: "issuerName",
                cell: (cellProps: { row: { original: BondRow }; getValue: () => string | null }) => {
                    const { issuerId } = cellProps.row.original;
                    const issuerName = cellProps.getValue();
                    if (issuerId && issuerName) {
                        return <Link to={`/parties/${issuerId}`}>{issuerName}</Link>;
                    }
                    return issuerName || "N/A";
                }
            },
            {
                header: "Credit Rating",
                accessorKey: "creditRating",
                cell: (cellProps: { getValue: () => string | null }) => {
                    const value = cellProps.getValue();
                    if (!value || value === "Not Applicable") {
                        return "N/A";
                    }
                    return value;
                }
            },
            {
                header: "Issuer Program",
                accessorKey: "issuerProgramCategory",
                cell: (cellProps: { row: { original: BondRow }; getValue: () => string | null }) => {
                    const { issuerProgramId } = cellProps.row.original;
                    const category = cellProps.getValue();
                    if (!category || category === IssuerProgramCategory.None) {
                        return "N/A";
                    }
                    if (issuerProgramId) {
                        return <Link to={`/issuerprograms/${issuerProgramId}`}>{category}</Link>;
                    }
                    return category;
                }
            },
            {
                header: "Maturity Date",
                accessorKey: "maturityDate",
                cell: (cellProps: { getValue: () => string | null }) => cellProps.getValue() || "N/A"
            },
            {
                header: "Spread",
                accessorKey: "spread",
                filter: "number",
                cell: (cellProps: { getValue: () => number | null }) => {
                    const spread = cellProps.getValue();
                    if (typeof spread !== "number" || Number.isNaN(spread)) {
                        return <div style={{ textAlign: "right" }}>–</div>;
                    }
                    return <div style={{ textAlign: "right" }}>{spreadBpFormat(spread)}</div>;
                }
            },
            {
                header: "Yield",
                accessorKey: "yield",
                filter: "number",
                cell: (cellProps: { getValue: () => number | null }) => {
                    const ytm = cellProps.getValue();
                    if (typeof ytm !== "number" || Number.isNaN(ytm)) {
                        return <div style={{ textAlign: "right" }}>–</div>;
                    }
                    return <div style={{ textAlign: "right" }}>{`${yieldPctFormat(ytm)}%`}</div>;
                }
            },
            {
                header: "Position",
                accessorKey: "totalQuantity",
                filter: "number",
                cell: (cellProps: { row: { original: BondRow }; getValue: () => number }) => (
                    <div style={{ textAlign: "right" }}>
                        <Link to={`/instrumentpositions?instrumentIds=${cellProps.row.original.instrumentId}`}>
                            {twoDecPriceFormat(cellProps.getValue()) ?? "–"}
                        </Link>
                    </div>
                )
            },
            {
                header: "Clients",
                accessorKey: "clientNames",
                filter: "arrayMultiSelect",
                cell: (cellProps: { row: { original: BondRow } }) => {
                    const clients = cellProps.row.original.clients;
                    if (!clients.length) {
                        return "N/A";
                    }
                    const clientListText = clients.map((client) => client.name).join(", ");
                    return (
                        <span title={clientListText}>
                            {clients.map((client, index) => (
                                <React.Fragment key={client._id}>
                                    {index > 0 ? ", " : null}
                                    <Link to={`/parties/${client._id}`}>{client.name}</Link>
                                </React.Fragment>
                            ))}
                        </span>
                    );
                }
            }
        ],
        []
    );

    return (
        <Page className="bondspage">
            <div>
                <h4>Bonds</h4>
                <div className="row mb-2">
                    <div className="col-12 col-sm-4">
                        <DateForm defaultDateString={date} dateName={"date"} />
                    </div>
                </div>
                {error ? <p>Error loading bonds: {error.message}</p> : null}
                {fetching ? <p>Loading...</p> : <ReactTable2 columns={columns} data={bondRows} />}
            </div>
        </Page>
    );
};
